import React, { useEffect, useState } from "react";
import { HelpCircle, AlertTriangle, Building2, Info } from "lucide-react";
import { SaasSettingsType } from "../components/dataTypes";
import App from "App";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: () => void;
  helpText?: boolean;
}

const ToggleSwitch: React.FC<ToggleProps> = ({
  label,
  checked,
  onChange,
  helpText,
}) => {
  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={`${
          checked
            ? "bg-blue-600 dark:bg-blue-500"
            : "bg-gray-200 dark:bg-gray-700"
        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full 
        border-2 border-transparent transition-colors duration-200 ease-in-out 
        focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 
        dark:focus:ring-offset-gray-900`}
      >
        <span
          aria-hidden="true"
          className={`${
            checked ? "translate-x-5" : "translate-x-0"
          } pointer-events-none inline-block h-5 w-5 transform rounded-full 
          bg-white dark:bg-gray-300 shadow ring-0 transition duration-200 ease-in-out`}
        />
      </button>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </span>
        {helpText && (
          <HelpCircle className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
        )}
      </div>
    </div>
  );
};

interface SettingsPageProps {
  saadSettings: SaasSettingsType | null;
  handleRefresh: () => void;
}
const SettingsPage: React.FC<SettingsPageProps> = ({
  saadSettings,
  handleRefresh,
}) => {
  const AppServices = useAppServices();
  const { user } = useUserInfo();
  const [suspendOnFailedPayment, setSuspendOnFailedPayment] =
    useState<boolean>(false);
  const [allowSubscriptionUpgrade, setAllowSubscriptionUpgrade] =
    useState<boolean>(false);

  const [subAccountNamingConvention, setSubAccountNamingConvention] = useState<
    "company" | "customer"
  >("customer");

  useEffect(() => {
    setSuspendOnFailedPayment(
      saadSettings?.advanced.suspendOnFailedPayment === true
    );
    setAllowSubscriptionUpgrade(
      saadSettings?.advanced?.allowSubscriptionUpgrade === true
    );
    setSubAccountNamingConvention(
      saadSettings?.advanced?.subAccountNamingConvention || "customer"
    );
  }, [saadSettings]);
  const handleSubmit = async () => {
    try {
      const settings = {
        suspendOnFailedPayment,
        allowSubscriptionUpgrade,
        subAccountNamingConvention,
      };

      let payload: any = {
        advanced: settings,
      };

      if (saadSettings && saadSettings._id) {
        payload["_id"] = saadSettings._id;
        const { response } = await AppServices.saas_setting.update({
          payload,
          toaster: true,
        });
      } else {
        payload["companyId"] = user?.companyId;
        const { response } = await AppServices.saas_setting.create({
          payload,
          toaster: true,
        });
      }

      handleRefresh();
    } catch (error: any) {}
  };

  return (
    <div className="p-8">
      {/* Top Header */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-slate-900 dark:text-white">
          Advanced Settings
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Controls for how sub-account admins can manage their subscription.
        </p>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 my-6"></div>

      {/* Section 1 */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Left column */}
        <div className="md:col-span-4">
          <h3 className="text-base font-medium text-slate-900 dark:text-white">
            Subscription Settings
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage upgrades, downgrades and suspensions.
          </p>
        </div>

        {/* Right column */}
        <div className="md:col-span-8 space-y-5">
          <ToggleSwitch
            label="Suspend sub-account when SaaS subscription fails"
            checked={suspendOnFailedPayment}
            onChange={() => setSuspendOnFailedPayment(!suspendOnFailedPayment)}
          />

          <ToggleSwitch
            label="Allow sub-account admins to upgrade their subscription"
            checked={allowSubscriptionUpgrade}
            onChange={() =>
              setAllowSubscriptionUpgrade(!allowSubscriptionUpgrade)
            }
            helpText={true}
          />
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 my-10"></div>

      {/* Section 2 */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <h3 className="text-base font-medium text-slate-900 dark:text-white">
            Sub-Account Onboarding
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Customize naming convention and welcome email for new sub-accounts
          </p>
        </div>

        <div className="md:col-span-8">
          <div
            className="border border-gray-200 dark:border-gray-700 
          rounded-lg p-6 shadow-sm bg-white dark:bg-gray-800"
          >
            <div className="flex items-center gap-3 mb-5">
              <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h4 className="text-base font-medium text-slate-900 dark:text-white">
                Choose naming convention for new SaaS sub-accounts
              </h4>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              {/* Company Name option */}
              <div
                onClick={() => setSubAccountNamingConvention("company")}
                className={`
                  flex-1 flex items-center justify-between p-4 border rounded-md cursor-pointer transition-all
                  ${
                    subAccountNamingConvention === "company"
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400"
                      : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                  }
                `}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-700 dark:text-gray-200">
                    Use Company Name
                  </span>
                  <Info className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                </div>

                <div
                  className={`h-4 w-4 rounded-full border flex items-center justify-center ${
                    subAccountNamingConvention === "company"
                      ? "border-blue-500 dark:border-blue-400"
                      : "border-gray-300 dark:border-gray-500"
                  }`}
                >
                  {subAccountNamingConvention === "company" && (
                    <div className="h-2 w-2 bg-blue-600 dark:bg-blue-400 rounded-full" />
                  )}
                </div>
              </div>

              {/* Customer Name option */}
              <div
                onClick={() => setSubAccountNamingConvention("customer")}
                className={`
                  flex-1 flex items-center justify-between p-4 border rounded-md cursor-pointer transition-all
                  ${
                    subAccountNamingConvention === "customer"
                      ? "border-blue-500 bg-blue-50/30 dark:bg-blue-900/40 dark:border-blue-400"
                      : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                  }
                `}
              >
                <span className="text-sm text-blue-700 dark:text-blue-400 font-medium">
                  Use Customer Name
                </span>

                <div
                  className={`h-4 w-4 rounded-full border flex items-center justify-center ${
                    subAccountNamingConvention === "customer"
                      ? "border-blue-500 dark:border-blue-400"
                      : "border-gray-300 dark:border-gray-500"
                  }`}
                >
                  {subAccountNamingConvention === "customer" && (
                    <div className="h-2 w-2 bg-blue-600 dark:bg-blue-400 rounded-full" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ----------------------------- */}
      {/* Submit Button */}
      {/* ----------------------------- */}
      <div className="mt-10">
        <button
          onClick={handleSubmit}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
